import { useState, useCallback, useEffect, useRef } from "react";
import { useGetExceptionalRewards } from "../../domain/hooks/use_get_exceptional_rewards";
import ExclusiveRewardElement from "./exclusive_reward_element";
import ThemeConstants from "../../../../config/theme/theme_constants";
import { RewardInterface } from "../../../home/domain/interface/reward_interface";
import ExclusiveRewardDialog from "./exclusive_reward_dialog";
import { SyncLoader } from "react-spinners";
import { useTranslation } from "../../../../i18n/useTranslation";

const ExclusiveRewardsList: React.FC<{ filters: string[] }> = ({ filters }) => {
    const { t } = useTranslation();
    const loadMoreRef = useRef<HTMLDivElement>(null);
    const { 
        allExceptionalRewards, 
        isLoading, 
        error, 
        refetch, 
        fetchNextPage, 
        hasNextPage, 
        isFetchingNextPage 
    } = useGetExceptionalRewards(10, filters);
    const [ showDialog, setShowDialog ] = useState<RewardInterface | null>(null);
    const theme = ThemeConstants();

    const handleLoadMore = useCallback(() => {
        if (hasNextPage && !isFetchingNextPage) {
            fetchNextPage();
        }
    }, [hasNextPage, isFetchingNextPage, fetchNextPage]);

    useEffect(() => {
        const observer = new IntersectionObserver(
            (entries) => {
                const [entry] = entries;
                if (entry.isIntersecting) {
                    handleLoadMore();
                }
            },
            { threshold: 0.1 }
        );

        const loadMoreElement = loadMoreRef.current;
        if (loadMoreElement) {
            observer.observe(loadMoreElement);
        }

        return () => {
            if (loadMoreElement) {
                observer.unobserve(loadMoreElement);
            }
        };
    }, [handleLoadMore]);

    if (isLoading) {
        return <SyncLoader color={theme.UvibesPink} size={16} speedMultiplier={0.5}/>;
    } else if (error) {
        return (
            <div className="flex flex-col gap-4 items-center">
                <span className="text-sm text-medium text-center">{t('common.error.occurred')}</span>
                <button
                    className="px-4 py-2 rounded-[18px] text-white font-semibold text-sm cursor-pointer"
                    style={{ background: theme.UvibesPink }}
                    onClick={() => refetch()}
                >
                    {t('common.button.retry')}
                </button>
            </div>
        );
    } else if (allExceptionalRewards.length === 0) {
        return <span className="text-medium text-center">{filters.length > 0 ? t('rewards.no_results') : t('rewards.none_yet')}</span>;
    }
    return (
        <>
            <ExclusiveRewardDialog
                onClose={() => setShowDialog(null)}
                reward={showDialog}
            />
            <div className="flex flex-col gap-4 w-full max-w-143.5">
                <div className="flex flex-wrap gap-3.5 justify-center">
                    {allExceptionalRewards.map((reward, index) => 
                        <ExclusiveRewardElement 
                            key={index} 
                            reward={reward} 
                            experience={reward.expGained || 0} 
                            onClick={() => setShowDialog(reward)}
                        />
                    )}
                </div>
                
                {hasNextPage && (
                    <div ref={loadMoreRef} className="flex justify-center py-4">
                        {isFetchingNextPage ? (
                            <SyncLoader color={theme.UvibesPink} size={16} speedMultiplier={0.5}/>
                        ) : (
                            <button 
                                onClick={handleLoadMore}
                                className="text-sm font-medium px-6 py-2 rounded-full border border-gray-300 hover:bg-gray-50"
                            >
                                {t('rewards.load_more')}
                            </button>
                        )}
                    </div>
                )}
            </div>
        </>
    );
}

export default ExclusiveRewardsList;